import "./BundleData.scss";

import React, { useEffect, useState } from 'react';
import { BundleDataProps, BundleProps } from "./BundleDataTypes";
import { postApiCreateBundle, putApiUpdateBundle } from "../../../services/apiBundle";
import { getApiCourses } from "../../../services/apiCourse";
import InputText from "../../InputText/InputText";
import Button from "../../Button/Button";
import CheckBox from "../../CheckBox/CheckBox";
import UploadPicture from "../../UploadPicture/UploadPicture";
import { useNavigate } from "react-router-dom";
import ls from "../../../services/localStorage";
import { types } from "../../../constants/constants";

interface CourseItem {
  _id: string;
  title: string;
}

const BundleData = (BundleDataProps: BundleDataProps): React.ReactElement => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState<boolean>(false);
  const [bundle, setBundle] = useState<BundleProps>(BundleDataProps.bundle);
  const [selectedCourses, setSelectedCourses] = useState<string[]>(BundleDataProps.bundle && BundleDataProps.bundle.courses ? BundleDataProps.bundle.courses : []);
  const [courses, setCourses] = useState<CourseItem[]>([]);
  const [formErrors, setFormErrors] = useState<any>();

  useEffect(() => {
    // Load available courses
    const loadCourses = async () => {
      try {
        const response: any = await getApiCourses({
          limit: 100,
          offset: 0,
          sortBy: 'title',
          sortDirection: 1
        });
        setCourses(response.courses || []);
      } catch (error) {
        console.error('Error loading courses:', error);
      }
    };

    loadCourses();
  }, []);

  useEffect(() => {
    // Sync bundle state when prop changes (for editing)
    if (BundleDataProps.bundle && BundleDataProps.bundle._id) {
      setBundle(BundleDataProps.bundle);
      if (BundleDataProps.bundle.courses && Array.isArray(BundleDataProps.bundle.courses)) {
        // Extract IDs if courses are objects, otherwise use as is
        const courseIds = BundleDataProps.bundle.courses.map((course: any) =>
          typeof course === 'string' ? course : course._id
        );
        setSelectedCourses(courseIds);
      }
    }
  }, [BundleDataProps.bundle?._id]);

  const saveBundleData = async(): Promise<void> => {
    setSaving(true);

    try {
      let error = false;
      setFormErrors((formErrors: any) => Object.assign({}));

      if (!bundle.title) {
        setFormErrors((formErrors: any) => Object.assign({}, formErrors, { title: "El título es obligatorio" }));
        error = true;
      }

      if (!bundle.description) {
        setFormErrors((formErrors: any) => Object.assign({}, formErrors, { description: "La descripción es obligatoria" }));
        error = true;
      }

      if (!bundle.price) {
        setFormErrors((formErrors: any) => Object.assign({}, formErrors, { price: "El precio es obligatorio" }));
        error = true;
      }

      if (selectedCourses.length === 0) {
        setFormErrors((formErrors: any) => Object.assign({}, formErrors, { courses: "Debes seleccionar al menos un curso" }));
        error = true;
      }

      if (!error) {
        const token = ls.get("token");
        setFormErrors((formErrors: any) => Object.assign({}, formErrors, { form: "" }));

        let responsePictureUploadJson: any = {};
        let responsePictureLandingUploadJson: any = {};

        // Upload image for listing
        if (bundle.picture) {
          let formDataImage = new FormData();
          formDataImage.append("file", bundle && bundle.picture ? bundle.picture[0] : '');

          let responsePictureUpload = await fetch(`${types.API_URL}upload`, {
            method: "POST",
            headers: { token: token ? token : '' },
            body: formDataImage
          });

          if (!responsePictureUpload.ok) {
            alert(await responsePictureUpload.json());
          }

          responsePictureUploadJson = await responsePictureUpload.json();
        }

        // Upload image for landing (only if it's a new file, not a URL string)
        if (bundle.picture_landing && bundle.picture_landing[0]) {
          let formDataImageLanding = new FormData();
          formDataImageLanding.append("file", bundle.picture_landing[0]);

          let responsePictureLandingUpload = await fetch(`${types.API_URL}upload`, {
            method: "POST",
            headers: { token: token ? token : '' },
            body: formDataImageLanding
          });

          if (!responsePictureLandingUpload.ok) {
            alert(await responsePictureLandingUpload.json());
          }

          responsePictureLandingUploadJson = await responsePictureLandingUpload.json();
        }

        const attributes: any = {
          bundle: {
            title: bundle.title,
            description: bundle.description,
            price: bundle.price,
            courses: selectedCourses,
            active: bundle.active !== false,
            picture_big: bundle.picture ? responsePictureUploadJson.url_big : bundle.picture_big,
            picture_medium: bundle.picture ? responsePictureUploadJson.url_medium : bundle.picture_medium,
            picture_thumb: bundle.picture ? responsePictureUploadJson.url_thumb : bundle.picture_thumb,
            picture_landing: bundle.picture_landing_deleted ? null : (bundle.picture_landing && bundle.picture_landing[0] ? responsePictureLandingUploadJson.url_big : (bundle.picture_landing_url || bundle.picture_landing)),
            titulo_cta: bundle.titulo_cta,
            descripcion_cta: bundle.descripcion_cta
          }
        };

        if (bundle._id && bundle._id !== '') {
          attributes.bundle._id = bundle._id;
          putApiUpdateBundle(attributes)
            .then(async () => {
              setSaving(false);
              navigate('/bundles');
            })
            .catch((error: any) => {
              setSaving(false);
              console.log(error.message);
            });
        } else {
          postApiCreateBundle(attributes)
            .then(async () => {
              setSaving(false);
              navigate('/bundles');
            })
            .catch((error: any) => {
              setSaving(false);
              console.log(error.message);
            });
        }
      } else {
        setFormErrors((formErrors: any) => Object.assign({}, formErrors, { form: "Hay errores en el formulario" }));
        console.log(formErrors);
        setSaving(false);
      }
    } catch (error: any) {
      alert(error.stack);
    }
  };

  const handleCheckbox = (event: any) => {
    if (selectedCourses.find((c) => c === event.currentTarget.value)) {
      setSelectedCourses(selectedCourses.filter((c) => c !== event.currentTarget.value));
    } else {
      setSelectedCourses([...selectedCourses, event.currentTarget.value]);
    }
  };

  return (
    <React.Fragment>
      <div className="row mb-5">
        <div className="col-md-9 my-3">
          <InputText
            label="Nombre *"
            placeholder="Nombre del bundle"
            value={bundle.title}
            onChange={(event: any) => { setBundle({ ...bundle, title: event.currentTarget.value }) }} />

          { formErrors && formErrors.title &&
            <p className="error">{ formErrors.title }</p>
          }
        </div>
        
        <div className="col-md-3 my-3">
          <InputText
            label="Precio *"
            placeholder="99,99"
            type="number"
            value={bundle.price ? bundle.price.toString() : ''}
            onChange={(event: any) => { setBundle({ ...bundle, price: parseFloat(event.currentTarget.value) }) }} />

          { formErrors && formErrors.price &&
            <p className="error">{ formErrors.price }</p>
          }
        </div>

        {bundle._id && bundle._id !== '' &&
          <div className="col-md-12 my-3">
            <InputText
              label="Slug"
              placeholder="slug-del-bundle"
              value={bundle.slug ? bundle.slug : ''}
              readonly={true}
              onChange={(event: any) => { setBundle({ ...bundle, slug: event.currentTarget.value }) }} />
            <small className="text-muted">URL: /bundle/{bundle.slug}</small>
          </div>
        }

        <div className="col-12 my-3">
          <label className="mb-2">Descripción del bundle *</label>
          <textarea
            className="form-control"
            placeholder="Descripción del bundle"
            value={bundle.description ? bundle.description : ''}
            onChange={(event: any) => { setBundle({ ...bundle, description: event.currentTarget.value }) }}
            rows={5}
          />

          { formErrors && formErrors.description &&
            <p className="error">{ formErrors.description }</p>
          }
        </div>

        <div className="col-12 my-3">
          <InputText
            label="Título CTA"
            placeholder="Ej: ¡Compra ahora tu bundle!"
            value={bundle.titulo_cta ? bundle.titulo_cta : ''}
            onChange={(event: any) => { setBundle({ ...bundle, titulo_cta: event.currentTarget.value }) }} />
        </div>

        <div className="col-12 my-3">
          <label className="mb-2">Descripción CTA</label>
          <textarea
            className="form-control"
            placeholder="Descripción del CTA"
            value={bundle.descripcion_cta ? bundle.descripcion_cta : ''}
            onChange={(event: any) => { setBundle({ ...bundle, descripcion_cta: event.currentTarget.value }) }}
            rows={3}
          />
        </div>

        <div className="col-12 col-md-6 my-3">
          <label className="mb-2">Imagen del listado</label>
          <UploadPicture item={bundle} onChange={setBundle} />

          { formErrors && formErrors.picture &&
            <p className="error">{ formErrors.picture }</p>
          }
        </div>

        <div className="col-12 col-md-6 my-3">
          <label className="mb-2">Imagen de la landing</label>
          <UploadPicture
            item={{ picture: bundle.picture_landing, picture_big: bundle.picture_landing_url }}
            onChange={(updatedItem: any) => setBundle({ ...bundle, picture_landing: updatedItem.picture, picture_landing_url: updatedItem.picture_big })}
          />
          {bundle.picture_landing_url && (
            <div className="mt-2">
              <Button
                className="btn-danger btn-sm"
                value="Eliminar imagen"
                onClick={() => setBundle({ ...bundle, picture_landing_url: null, picture_landing: null, picture_landing_deleted: true })}
              />
            </div>
          )}

          { formErrors && formErrors.picture_landing &&
            <p className="error">{ formErrors.picture_landing }</p>
          }
        </div>

        <div className="col-12 my-3">
          <label className="mb-2">Cursos incluidos *</label>
          <div className="bundle-courses-selector">
            {courses.length > 0 ? (
              courses.map(course => (
                <CheckBox
                  key={course._id}
                  label={course.title}
                  name="courses"
                  value={course._id}
                  checked={selectedCourses && selectedCourses.indexOf(course._id) !== -1}
                  onChange={(event: any) => { handleCheckbox(event) }}
                />
              ))
            ) : (
              <p>No hay cursos disponibles</p>
            )}
          </div>

          { formErrors && formErrors.courses &&
            <p className="error">{ formErrors.courses }</p>
          }
        </div>

        <div className="col-12 my-3">
          <CheckBox
            label="Bundle activo"
            name="active"
            checked={bundle.active !== false}
            onChange={(event: any) => { setBundle({ ...bundle, active: event.currentTarget.checked }) }}
          />
        </div>
      </div>

      <div className="row pb-5">
        <div className="col text-center">
          { formErrors && formErrors.form &&
            <p className="error">{ formErrors.form }</p>
          }
          <Button value="Guardar" className="btn-orange mx-auto" onClick={saveBundleData} iconRight={ saving ? 'loaderIcon' : '' } />
        </div>
      </div>
    </React.Fragment>
  );
};

export default BundleData;
